// @ts-nocheck
import { useState, useEffect, useRef } from "react";
import { useListDevices } from "../lib/api-client-react";
import { PageHeader } from "../components/layout/PageHeader";
import { useUpload } from "../lib/object-storage-web";
import { useAiBot } from "../lib/ai-bot-context";

const API = import.meta.env.VITE_API_URL ?? "";

const MODELS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini (Fast & Affordable)" },
  { value: "gpt-4o", label: "GPT-4o (Smarter)" },
  { value: "gpt-5-mini", label: "GPT-5 Mini" },
];

const PROMPT_TEMPLATES = [
  { label: "General Customer Service", prompt: "You are a professional customer service agent. Be friendly, concise, and helpful. If you don't know something, say so honestly and offer to escalate to a human agent." },
  { label: "E-commerce Store", prompt: "You are a customer service agent for an online store. Help customers with order status, returns, product questions, and shipping. For complex issues, offer to connect them with our team." },
  { label: "Restaurant / Food", prompt: "You are a helpful assistant for our restaurant. Answer questions about the menu, hours, reservations, and delivery. For table bookings, collect name, date, time, and party size." },
  { label: "Real Estate", prompt: "You are a real estate assistant. Help clients with property inquiries, schedule viewings, and answer questions about listings. Collect their contact details and requirements." },
  { label: "Healthcare Clinic", prompt: "You are a patient coordinator. Help patients book appointments, answer general questions about services, and provide clinic hours. Never give specific medical advice." },
];

type Tab = "settings" | "training" | "conversations";

interface BotSettings { id: number | null; deviceId: number; enabled: boolean; businessName: string; systemPrompt: string; fallbackMessage: string; model: string; websiteUrl: string; }
interface TrainingRow { id: number; deviceId: number; question: string; answer: string; mediaUrl: string; mediaType: string; buttons: string[]; createdAt: string; }
interface ConvPreview { phone: string; messageCount: number; lastMessage: string; lastContent: string; lastRole: string; takenOver: boolean; }
interface Message { id: number; deviceId: number; phone: string; role: string; content: string; createdAt: string; }

// ─── On/Off Master Button ──────────────────────────────────────────────────

function PowerButton({ deviceId, enabled, onChange }: { deviceId: number; enabled: boolean; onChange: (v: boolean) => void }) {
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !enabled;
    setLoading(true);
    try {
      await fetch(`${API}/api/aibot/${deviceId}/toggle`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      onChange(next);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-xl p-5 border-2 transition-all ${enabled ? "bg-[#f0fff8] border-[#2dce89]" : "bg-[#fff5f5] border-[#f5365c]"}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Big power icon */}
          <button
            onClick={toggle}
            disabled={loading}
            title={enabled ? "Click to turn off" : "Click to turn on"}
            className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 ${
              enabled
                ? "bg-[#2dce89] hover:bg-[#26af74] shadow-[#2dce89]/40"
                : "bg-[#f5365c] hover:bg-[#d4284f] shadow-[#f5365c]/40"
            } disabled:opacity-60`}
          >
            {loading
              ? <i className="fas fa-circle-notch fa-spin text-white text-xl" />
              : <i className="fas fa-power-off text-white text-xl" />
            }
          </button>
          <div>
            <p className={`text-base font-bold ${enabled ? "text-[#1a7a4f]" : "text-[#b91c40]"}`}>
              AI Bot is {enabled ? "ON" : "OFF"}
            </p>
            <p className={`text-sm mt-0.5 ${enabled ? "text-[#2dce89]" : "text-[#f5365c]"}`}>
              {enabled
                ? "Replying automatically to incoming messages"
                : "Not replying — click the button to activate"}
            </p>
          </div>
        </div>
        <div className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${enabled ? "bg-[#2dce89] text-white" : "bg-[#f5365c] text-white"}`}>
          {enabled ? "Active" : "Paused"}
        </div>
      </div>
    </div>
  );
}

// ─── Settings Panel ─────────────────────────────────────────────────────────

function SettingsPanel({ deviceId }: { deviceId: number }) {
  const { enabled: contextEnabled, syncEnabled } = useAiBot();
  const [settings, setSettings] = useState<BotSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [testingUrl, setTestingUrl] = useState(false);
  const [urlStatus, setUrlStatus] = useState<"idle" | "ok" | "error">("idle");

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/aibot/${deviceId}`).then(r => r.json()).then(data => {
      setSettings(data);
      syncEnabled(data?.enabled ?? false);
    }).finally(() => setLoading(false));
  }, [deviceId]);

  // Sync from sidebar toggle → update local settings to match context
  useEffect(() => {
    if (contextEnabled !== null && settings !== null && settings.enabled !== contextEnabled) {
      setSettings(p => p ? { ...p, enabled: contextEnabled } : null);
    }
  }, [contextEnabled]);

  const update = (field: keyof BotSettings, value: any) => { setSettings(p => p ? { ...p, [field]: value } : null); setSaved(false); };
  const onEnabledChange = (v: boolean) => { setSettings(p => p ? { ...p, enabled: v } : null); syncEnabled(v); };

  const testUrl = async () => {
    if (!settings?.websiteUrl?.trim()) return;
    setTestingUrl(true); setUrlStatus("idle");
    try {
      const r = await fetch(`${API}/api/test-url?url=${encodeURIComponent(settings.websiteUrl)}`, { signal: AbortSignal.timeout(10000) });
      const data = await r.json();
      setUrlStatus(data.ok ? "ok" : "error");
    } catch { setUrlStatus("error"); } finally { setTestingUrl(false); }
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/aibot/${deviceId}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(settings),
      });
      setSettings(await r.json()); setSaved(true); setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  if (loading) return <Spinner />;
  if (!settings) return null;

  return (
    <div className="space-y-4">
      {/* On/Off master button */}
      <PowerButton deviceId={deviceId} enabled={settings.enabled} onChange={onEnabledChange} />

      {/* Business + Model */}
      <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-5 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-[#525f7f] mb-1.5"><i className="fas fa-building mr-1.5 text-[#1171ef]" />Business Name</label>
          <input type="text" value={settings.businessName} onChange={e => update("businessName", e.target.value)} placeholder="e.g. Acme Store, Tel Aviv Clinic…" className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" />
          <p className="text-xs text-[#8898aa] mt-1">The AI will introduce itself as a representative of this business.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#525f7f] mb-1.5"><i className="fas fa-cog mr-1.5 text-[#1171ef]" />AI Model</label>
          <select value={settings.model} onChange={e => update("model", e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]">
            {MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>

      {/* Website URL */}
      <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-5">
        <div className="mb-3">
          <label className="block text-xs font-semibold text-[#525f7f] mb-0.5"><i className="fas fa-globe mr-1.5 text-[#1171ef]" />Website Knowledge Base</label>
          <p className="text-xs text-[#8898aa]">Add your website URL and the AI will read its content to answer customer questions accurately based on your real information.</p>
        </div>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="url"
              value={settings.websiteUrl}
              onChange={e => { update("websiteUrl", e.target.value); setUrlStatus("idle"); }}
              placeholder="https://yourwebsite.com"
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 pr-8 ${
                urlStatus === "ok" ? "border-[#2dce89] focus:ring-[#2dce89]/30" :
                urlStatus === "error" ? "border-[#f5365c] focus:ring-[#f5365c]/30" :
                "border-[#cad1d7] focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
              }`}
            />
            {urlStatus === "ok" && <i className="fas fa-check-circle text-[#2dce89] absolute right-2.5 top-2.5 text-sm" />}
            {urlStatus === "error" && <i className="fas fa-times-circle text-[#f5365c] absolute right-2.5 top-2.5 text-sm" />}
          </div>
          <button
            onClick={testUrl}
            disabled={testingUrl || !settings.websiteUrl?.trim()}
            className="px-3 py-2 text-xs border border-[#cad1d7] rounded-lg text-[#525f7f] hover:border-[#1171ef] hover:text-[#1171ef] disabled:opacity-40 transition-colors whitespace-nowrap flex items-center gap-1.5"
          >
            {testingUrl ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-plug" />}
            Test
          </button>
        </div>
        {urlStatus === "ok" && <p className="text-xs text-[#2dce89] mt-1.5 flex items-center gap-1"><i className="fas fa-check-circle" />Website reachable — the AI will pull information from this page.</p>}
        {urlStatus === "error" && <p className="text-xs text-[#f5365c] mt-1.5 flex items-center gap-1"><i className="fas fa-exclamation-circle" />Could not reach the website. Check the URL and try again.</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          <p className="text-xs text-[#8898aa] w-full">Examples:</p>
          {["https://example.com", "https://yourstore.com/about", "https://clinic.com/services"].map(ex => (
            <button key={ex} onClick={() => { update("websiteUrl", ex); setUrlStatus("idle"); }} className="text-xs text-[#1171ef] bg-[#eef0fd] px-2 py-0.5 rounded hover:bg-[#d8dcff]">{ex}</button>
          ))}
        </div>
      </div>

      {/* System prompt */}
      <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <label className="block text-xs font-semibold text-[#525f7f]"><i className="fas fa-brain mr-1.5 text-[#1171ef]" />AI Instructions (System Prompt)</label>
            <p className="text-xs text-[#8898aa] mt-0.5">Tell the AI who it is, what it knows, and how to behave.</p>
          </div>
          <button onClick={() => setShowTemplates(!showTemplates)} className="text-xs text-[#1171ef] hover:underline flex items-center gap-1"><i className="fas fa-magic" />Template</button>
        </div>
        {showTemplates && (
          <div className="border border-[#e9ecef] rounded-lg p-3 bg-[#f8f9fe] space-y-2">
            {PROMPT_TEMPLATES.map(p => (
              <button key={p.label} onClick={() => { update("systemPrompt", p.prompt); setShowTemplates(false); }} className="w-full text-left px-3 py-2 bg-white rounded-lg border border-[#e9ecef] hover:border-[#1171ef] hover:bg-[#f0f4ff] transition-colors">
                <p className="text-xs font-medium text-[#32325d]">{p.label}</p>
                <p className="text-xs text-[#8898aa] mt-0.5 line-clamp-1">{p.prompt}</p>
              </button>
            ))}
          </div>
        )}
        <textarea value={settings.systemPrompt} onChange={e => update("systemPrompt", e.target.value)} rows={5} placeholder="Example: You are a customer service agent for Acme Store. We sell electronics online…" className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] resize-none" />
      </div>

      <div className="flex items-center justify-end gap-3">
        {saved && <span className="text-sm text-[#2dce89] flex items-center gap-1.5"><i className="fas fa-check-circle" />Saved</span>}
        <button onClick={save} disabled={saving} className="gradient-primary text-white px-6 py-2.5 rounded-lg text-sm font-medium shadow-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2">
          {saving ? <><i className="fas fa-circle-notch fa-spin" />Saving…</> : <><i className="fas fa-save" />Save Settings</>}
        </button>
      </div>
    </div>
  );
}

// ─── Training Panel ──────────────────────────────────────────────────────────

type FormMediaType = "" | "image" | "video";

function TrainingPanel({ deviceId }: { deviceId: number }) {
  const [rows, setRows] = useState<TrainingRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [mediaType, setMediaType] = useState<FormMediaType>("");
  const [uploadedPath, setUploadedPath] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [buttons, setButtons] = useState<string[]>([]);
  const [newBtn, setNewBtn] = useState("");
  const [adding, setAdding] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Edit state
  const [editId, setEditId] = useState<number | null>(null);
  const [editQ, setEditQ] = useState("");
  const [editA, setEditA] = useState("");
  const [editMediaType, setEditMediaType] = useState<FormMediaType>("");
  const [editUploadedPath, setEditUploadedPath] = useState("");
  const [editPreviewUrl, setEditPreviewUrl] = useState("");
  const [editButtons, setEditButtons] = useState<string[]>([]);
  const [editNewBtn, setEditNewBtn] = useState("");
  const editFileRef = useRef<HTMLInputElement>(null);

  const { uploadFile, isUploading, progress } = useUpload({
    basePath: `${API}/api/storage`,
    onSuccess: (res) => setUploadedPath(res.objectPath),
  });

  const { uploadFile: uploadFileEdit, isUploading: isUploadingEdit, progress: progressEdit } = useUpload({
    basePath: `${API}/api/storage`,
    onSuccess: (res) => setEditUploadedPath(res.objectPath),
  });

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/aibot/${deviceId}/training`).then(r => r.json()).then(setRows).finally(() => setLoading(false));
  };
  useEffect(load, [deviceId]);

  const resetForm = () => {
    setQuestion(""); setAnswer(""); setMediaType(""); setUploadedPath(""); setPreviewUrl(""); setButtons([]); setNewBtn("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const add = async () => {
    if (!question.trim() || !answer.trim()) return;
    if ((mediaType === "image" || mediaType === "video") && !uploadedPath) return;
    setAdding(true);
    try {
      const r = await fetch(`${API}/api/aibot/${deviceId}/training`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, answer, mediaUrl: uploadedPath, mediaType, buttons }),
      });
      const newRow = await r.json();
      setRows(p => [...p, newRow]); resetForm();
    } finally { setAdding(false); }
  };

  const del = async (id: number) => {
    await fetch(`${API}/api/aibot/${deviceId}/training/${id}`, { method: "DELETE" });
    setRows(p => p.filter(r => r.id !== id));
  };

  const startEdit = (row: TrainingRow) => {
    setEditId(row.id); setEditQ(row.question); setEditA(row.answer);
    setEditMediaType((row.mediaType || "") as FormMediaType);
    setEditUploadedPath(row.mediaUrl || ""); setEditPreviewUrl(row.mediaUrl ? `${API}/api/storage${row.mediaUrl}` : "");
    setEditButtons(row.buttons || []); setEditNewBtn("");
  };

  const saveEdit = async (id: number) => {
    const r = await fetch(`${API}/api/aibot/${deviceId}/training/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question: editQ, answer: editA, mediaUrl: editUploadedPath, mediaType: editMediaType, buttons: editButtons }),
    });
    const updated = await r.json();
    setRows(p => p.map(row => row.id === id ? updated : row));
    setEditId(null);
  };

  const mediaStorageUrl = (path: string) => path ? `${API}/api/storage${path}` : "";

  const isDirectResponse = (row: TrainingRow) => !!row.mediaUrl || (row.buttons?.length > 0);

  return (
    <div className="space-y-4">
      {/* Info */}
      <div className="bg-[#f0f4ff] border border-[#d0daff] rounded-xl p-4 flex gap-3">
        <i className="fas fa-graduation-cap text-[#1171ef] text-lg mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-[#32325d]">Train your AI with Q&A examples</p>
          <p className="text-xs text-[#525f7f] mt-0.5">
            <strong>Text-only</strong> entries teach the AI your tone and facts. Entries with <strong>images, videos, or buttons</strong> are sent as direct replies whenever the keyword is matched — great for menus, price lists, or CTAs.
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-5">
        <h4 className="text-sm font-semibold text-[#32325d] mb-4"><i className="fas fa-plus-circle text-[#1171ef] mr-1.5" />Add Training Example</h4>
        <div className="space-y-4">

          {/* Question */}
          <div>
            <label className="block text-xs font-semibold text-[#525f7f] mb-1">Trigger keyword / question</label>
            <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="e.g. delivery time, price list, menu, hours…" className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" />
            <p className="text-xs text-[#8898aa] mt-1">When a customer's message contains this keyword, this response will be triggered.</p>
          </div>

          {/* Answer text */}
          <div>
            <label className="block text-xs font-semibold text-[#525f7f] mb-1">Response text</label>
            <textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={3} placeholder="The reply the bot will send when this keyword is detected…" className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] resize-none" />
          </div>

          {/* Media type selector */}
          <div>
            <label className="block text-xs font-semibold text-[#525f7f] mb-2"><i className="fas fa-paperclip mr-1.5 text-[#1171ef]" />Attach media (optional)</label>
            <div className="flex gap-2 mb-3">
              {(["", "image", "video"] as FormMediaType[]).map(t => (
                <button key={t} onClick={() => { setMediaType(t); setUploadedPath(""); setPreviewUrl(""); if (fileRef.current) fileRef.current.value = ""; }}
                  className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-colors ${mediaType === t ? "border-[#1171ef] bg-[#eef0fd] text-[#1171ef]" : "border-[#cad1d7] text-[#8898aa] hover:border-[#1171ef]"}`}>
                  <i className={`${t === "" ? "fas fa-font" : t === "image" ? "fas fa-image" : "fas fa-video"} mr-1.5`} />
                  {t === "" ? "Text only" : t === "image" ? "Image" : "Video"}
                </button>
              ))}
            </div>
            {(mediaType === "image" || mediaType === "video") && (
              <div>
                <input ref={fileRef} type="file" accept={mediaType === "image" ? "image/*" : "video/*"} className="hidden" onChange={async e => {
                  const file = e.target.files?.[0]; if (!file) return;
                  setPreviewUrl(URL.createObjectURL(file));
                  await uploadFile(file);
                }} />
                <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-[#cad1d7] rounded-lg py-6 text-center hover:border-[#1171ef] hover:bg-[#f0f4ff] transition-colors">
                  {isUploading ? (
                    <div className="space-y-1">
                      <i className="fas fa-circle-notch fa-spin text-[#1171ef] text-xl block" />
                      <p className="text-xs text-[#8898aa]">Uploading… {progress}%</p>
                    </div>
                  ) : previewUrl ? (
                    <div className="space-y-2">
                      {mediaType === "image"
                        ? <img src={previewUrl} className="h-24 mx-auto rounded-lg object-cover" alt="preview" />
                        : <video src={previewUrl} className="h-24 mx-auto rounded-lg" />}
                      <p className="text-xs text-[#2dce89]"><i className="fas fa-check-circle mr-1" />Uploaded — click to replace</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <i className={`${mediaType === "image" ? "fas fa-image" : "fas fa-video"} text-2xl text-[#cad1d7] block`} />
                      <p className="text-xs text-[#8898aa]">Click to upload {mediaType}</p>
                    </div>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div>
            <label className="block text-xs font-semibold text-[#525f7f] mb-1"><i className="fas fa-hand-pointer mr-1.5 text-[#1171ef]" />Interactive buttons (optional, max 3)</label>
            <p className="text-xs text-[#8898aa] mb-2">Customers tap a button and its text arrives as a message. Use for menus, options, or quick choices.</p>
            <div className="space-y-2">
              {buttons.map((btn, i) => (
                <div key={i} className="flex items-center gap-2 bg-[#f8f9fe] border border-[#e9ecef] rounded-lg px-3 py-2">
                  <i className="fas fa-hand-pointer text-[#1171ef] text-xs shrink-0" />
                  <span className="text-sm flex-1 text-[#32325d]">{btn}</span>
                  <button onClick={() => setButtons(b => b.filter((_, j) => j !== i))} className="text-[#8898aa] hover:text-[#f5365c]"><i className="fas fa-times text-xs" /></button>
                </div>
              ))}
              {buttons.length < 3 && (
                <div className="flex gap-2">
                  <input value={newBtn} onChange={e => setNewBtn(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && newBtn.trim()) { setButtons(b => [...b, newBtn.trim()]); setNewBtn(""); } }}
                    placeholder="Button label (e.g. See menu, Order now, Call us)" className="flex-1 border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" />
                  <button onClick={() => { if (newBtn.trim()) { setButtons(b => [...b, newBtn.trim()]); setNewBtn(""); } }} disabled={!newBtn.trim()} className="px-3 py-2 bg-[#eef0fd] text-[#1171ef] rounded-lg text-sm hover:bg-[#d8dcff] disabled:opacity-40">
                    <i className="fas fa-plus" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <button onClick={add} disabled={adding || !question.trim() || !answer.trim() || ((mediaType === "image" || mediaType === "video") && !uploadedPath)} className="gradient-primary text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-2">
            {adding ? <><i className="fas fa-circle-notch fa-spin" />Adding…</> : <><i className="fas fa-plus" />Add Example</>}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] overflow-hidden">
        <div className="px-5 py-3 border-b border-[#e9ecef] bg-[#f8f9fe] flex items-center justify-between">
          <p className="text-xs font-semibold text-[#525f7f] uppercase tracking-wider">Examples ({rows.length})</p>
          {rows.length > 0 && <p className="text-xs text-[#8898aa]">{rows.filter(r => !isDirectResponse(r)).length} AI training · {rows.filter(isDirectResponse).length} direct responses</p>}
        </div>
        {loading ? <div className="p-8 text-center"><Spinner /></div> : rows.length === 0 ? (
          <div className="p-12 text-center">
            <i className="fas fa-chalkboard-teacher text-3xl text-[#cad1d7] mb-2 block" />
            <p className="text-[#8898aa] text-sm">No training examples yet. Add your first one above.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#e9ecef]">
            {rows.map(row => (
              <div key={row.id} className="p-4">
                {editId === row.id ? (
                  /* ─ Edit mode ─ */
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-[#525f7f] mb-1 block">Keyword</label>
                      <input value={editQ} onChange={e => setEditQ(e.target.value)} className="w-full border border-[#1171ef] rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#525f7f] mb-1 block">Response text</label>
                      <textarea value={editA} onChange={e => setEditA(e.target.value)} rows={2} className="w-full border border-[#1171ef] rounded-lg px-3 py-1.5 text-sm focus:outline-none resize-none" />
                    </div>
                    {/* Edit media type */}
                    <div>
                      <label className="text-xs font-semibold text-[#525f7f] mb-1 block">Media</label>
                      <div className="flex gap-2 mb-2">
                        {(["", "image", "video"] as FormMediaType[]).map(t => (
                          <button key={t} onClick={() => { setEditMediaType(t); if (!t) { setEditUploadedPath(""); setEditPreviewUrl(""); } }}
                            className={`flex-1 py-1.5 rounded-lg border text-xs font-medium ${editMediaType === t ? "border-[#1171ef] bg-[#eef0fd] text-[#1171ef]" : "border-[#cad1d7] text-[#8898aa]"}`}>
                            {t === "" ? "None" : t === "image" ? "Image" : "Video"}
                          </button>
                        ))}
                      </div>
                      {(editMediaType === "image" || editMediaType === "video") && (
                        <div>
                          <input ref={editFileRef} type="file" accept={editMediaType === "image" ? "image/*" : "video/*"} className="hidden" onChange={async e => {
                            const file = e.target.files?.[0]; if (!file) return;
                            setEditPreviewUrl(URL.createObjectURL(file));
                            await uploadFileEdit(file);
                          }} />
                          <button onClick={() => editFileRef.current?.click()} className="w-full border-2 border-dashed border-[#cad1d7] rounded-lg py-4 text-center hover:border-[#1171ef] hover:bg-[#f0f4ff] transition-colors">
                            {isUploadingEdit ? <><i className="fas fa-circle-notch fa-spin text-[#1171ef]" /><span className="text-xs text-[#8898aa] ml-2">{progressEdit}%</span></> :
                              editPreviewUrl ? (editMediaType === "image" ? <img src={editPreviewUrl} className="h-16 mx-auto rounded object-cover" /> : <video src={editPreviewUrl} className="h-16 mx-auto rounded" />) :
                              <span className="text-xs text-[#8898aa]">Click to upload {editMediaType}</span>}
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Edit buttons */}
                    <div>
                      <label className="text-xs font-semibold text-[#525f7f] mb-1 block">Buttons</label>
                      <div className="space-y-1.5">
                        {editButtons.map((btn, i) => (
                          <div key={i} className="flex items-center gap-2 bg-[#f8f9fe] border border-[#e9ecef] rounded px-2 py-1.5">
                            <span className="text-xs flex-1">{btn}</span>
                            <button onClick={() => setEditButtons(b => b.filter((_, j) => j !== i))} className="text-[#8898aa] hover:text-[#f5365c]"><i className="fas fa-times text-xs" /></button>
                          </div>
                        ))}
                        {editButtons.length < 3 && (
                          <div className="flex gap-2">
                            <input value={editNewBtn} onChange={e => setEditNewBtn(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && editNewBtn.trim()) { setEditButtons(b => [...b, editNewBtn.trim()]); setEditNewBtn(""); } }}
                              placeholder="Button label" className="flex-1 border border-[#cad1d7] rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#1171ef]" />
                            <button onClick={() => { if (editNewBtn.trim()) { setEditButtons(b => [...b, editNewBtn.trim()]); setEditNewBtn(""); } }} className="px-2 py-1.5 bg-[#eef0fd] text-[#1171ef] rounded text-xs"><i className="fas fa-plus" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => saveEdit(row.id)} className="text-xs bg-[#1171ef] text-white px-4 py-1.5 rounded-lg hover:bg-[#4a5fc8]"><i className="fas fa-save mr-1" />Save</button>
                      <button onClick={() => setEditId(null)} className="text-xs text-[#8898aa] hover:text-[#525f7f] px-3 py-1.5">Cancel</button>
                    </div>
                  </div>
                ) : (
                  /* ─ View mode ─ */
                  <div className="flex gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Type badge */}
                      <div className="flex items-center gap-2 mb-2">
                        {isDirectResponse(row) ? (
                          <span className="text-xs bg-[#fdf0ff] text-[#1171ef] border border-[#e8c8ff] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                            <i className={row.mediaType === "image" ? "fas fa-image" : row.mediaType === "video" ? "fas fa-video" : "fas fa-hand-pointer"} />
                            Direct response
                          </span>
                        ) : (
                          <span className="text-xs bg-[#f0f4ff] text-[#1171ef] border border-[#d0daff] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                            <i className="fas fa-brain" />AI training
                          </span>
                        )}
                      </div>
                      <div className="flex items-start gap-2 mb-1">
                        <span className="text-xs font-bold text-[#1171ef] shrink-0 mt-0.5">Q</span>
                        <p className="text-sm text-[#32325d] font-medium">{row.question}</p>
                      </div>
                      <div className="flex items-start gap-2 mb-2">
                        <span className="text-xs font-bold text-[#2dce89] shrink-0 mt-0.5">A</span>
                        <p className="text-sm text-[#525f7f]">{row.answer}</p>
                      </div>
                      {/* Media preview */}
                      {row.mediaUrl && (
                        <div className="ml-4 mb-2">
                          {row.mediaType === "image"
                            ? <img src={mediaStorageUrl(row.mediaUrl)} className="h-20 rounded-lg object-cover border border-[#e9ecef]" alt="media" />
                            : <video src={mediaStorageUrl(row.mediaUrl)} className="h-20 rounded-lg border border-[#e9ecef]" controls={false} />}
                        </div>
                      )}
                      {/* Buttons preview */}
                      {row.buttons?.length > 0 && (
                        <div className="ml-4 flex flex-wrap gap-1.5">
                          {row.buttons.map((btn, i) => (
                            <span key={i} className="text-xs bg-[#eef0fd] text-[#1171ef] border border-[#c8d0ff] px-2 py-1 rounded-full flex items-center gap-1">
                              <i className="fas fa-hand-pointer text-xs" />{btn}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0 mt-1">
                      <button onClick={() => startEdit(row)} className="w-7 h-7 flex items-center justify-center rounded-lg text-[#8898aa] hover:bg-[#f0f4ff] hover:text-[#1171ef]"><i className="fas fa-pen text-xs" /></button>
                      <button onClick={() => del(row.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-[#8898aa] hover:bg-[#fff5f5] hover:text-[#f5365c]"><i className="fas fa-trash text-xs" /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Conversations Panel ─────────────────────────────────────────────────────

function ConversationsPanel({ deviceId }: { deviceId: number }) {
  const [contacts, setContacts] = useState<ConvPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [takenOver, setTakenOver] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadContacts = () => {
    fetch(`${API}/api/aibot/${deviceId}/conversations`).then(r => r.json()).then(data => { setContacts(data); setLoading(false); });
  };

  const loadMessages = (phone: string) => {
    fetch(`${API}/api/aibot/${deviceId}/conversations/${phone}`).then(r => r.json()).then(data => {
      setMessages(data.messages ?? []); setTakenOver(data.takenOver ?? false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
  };

  useEffect(() => { loadContacts(); const t = setInterval(loadContacts, 4000); return () => clearInterval(t); }, [deviceId]);
  useEffect(() => {
    if (!selectedPhone) return;
    loadMessages(selectedPhone);
    const t = setInterval(() => loadMessages(selectedPhone), 3000);
    return () => clearInterval(t);
  }, [selectedPhone, deviceId]);

  const handleTakeover = async () => {
    await fetch(`${API}/api/aibot/${deviceId}/conversations/${selectedPhone}/takeover`, { method: "POST" });
    setTakenOver(true); setContacts(p => p.map(c => c.phone === selectedPhone ? { ...c, takenOver: true } : c));
  };

  const handleRelease = async () => {
    await fetch(`${API}/api/aibot/${deviceId}/conversations/${selectedPhone}/release`, { method: "POST" });
    setTakenOver(false); setContacts(p => p.map(c => c.phone === selectedPhone ? { ...c, takenOver: false } : c));
  };

  const send = async () => {
    if (!reply.trim() || !selectedPhone) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/aibot/${deviceId}/conversations/${selectedPhone}/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: reply }) });
      const msg = await r.json();
      setMessages(p => [...p, msg]); setReply("");
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } finally { setSending(false); }
  };

  const roleColor = (r: string) => r === "user" ? "#525f7f" : r === "manager" ? "#fb6340" : "#1171ef";
  const roleLabel = (r: string) => r === "user" ? "Customer" : r === "manager" ? "Manager" : "AI";
  const roleIcon = (r: string) => r === "user" ? "fas fa-user" : r === "manager" ? "fas fa-user-shield" : "fas fa-robot";

  return (
    <div className="flex gap-4 h-[600px]">
      <div className="w-64 shrink-0 bg-white rounded-xl shadow-sm border border-[#e9ecef] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-[#e9ecef] bg-[#f8f9fe] flex items-center justify-between">
          <p className="text-xs font-semibold text-[#525f7f] uppercase tracking-wider">Conversations</p>
          <button onClick={loadContacts} className="text-[#8898aa] hover:text-[#1171ef]"><i className="fas fa-sync-alt text-xs" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <div className="p-6 text-center"><Spinner /></div> : contacts.length === 0 ? (
            <div className="p-6 text-center"><i className="fas fa-comments text-2xl text-[#cad1d7] mb-2 block" /><p className="text-xs text-[#8898aa]">No conversations yet</p></div>
          ) : contacts.map(c => (
            <button key={c.phone} onClick={() => { setSelectedPhone(c.phone); setMessages([]); }} className={`w-full text-left px-3 py-3 border-b border-[#f6f9fc] hover:bg-[#f8f9fe] transition-colors ${selectedPhone === c.phone ? "bg-[#eef0fd]" : ""}`}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#eef0fd] flex items-center justify-center shrink-0"><i className="fas fa-user text-xs text-[#1171ef]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <p className="text-xs font-semibold text-[#32325d] truncate">{c.phone}</p>
                    {c.takenOver && <span className="shrink-0 w-2 h-2 rounded-full bg-[#fb6340]" title="Manager active" />}
                  </div>
                  <p className="text-xs text-[#8898aa] truncate">{c.lastContent}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 bg-white rounded-xl shadow-sm border border-[#e9ecef] flex flex-col overflow-hidden">
        {!selectedPhone ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-full bg-[#f0f4ff] flex items-center justify-center mb-4"><i className="fas fa-comments text-2xl text-[#1171ef]" /></div>
            <p className="text-sm font-semibold text-[#32325d]">Select a conversation</p>
            <p className="text-xs text-[#8898aa] mt-1">View chat history and take over any conversation as manager.</p>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-[#e9ecef] bg-[#f8f9fe] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#eef0fd] flex items-center justify-center"><i className="fas fa-user text-xs text-[#1171ef]" /></div>
                <div>
                  <p className="text-sm font-semibold text-[#32325d]">{selectedPhone}</p>
                  <p className="text-xs">{takenOver ? <span className="text-[#fb6340] font-medium"><i className="fas fa-user-shield mr-1" />Manager mode</span> : <span className="text-[#2dce89]"><i className="fas fa-robot mr-1" />AI handling</span>}</p>
                </div>
              </div>
              {takenOver ? (
                <button onClick={handleRelease} className="text-xs bg-[#2dce89] text-white px-3 py-1.5 rounded-lg hover:bg-[#26af74] flex items-center gap-1.5"><i className="fas fa-robot" />Hand back to AI</button>
              ) : (
                <button onClick={handleTakeover} className="text-xs bg-[#fb6340] text-white px-3 py-1.5 rounded-lg hover:bg-[#e8502f] flex items-center gap-1.5"><i className="fas fa-user-shield" />Take Over</button>
              )}
            </div>
            {takenOver && (
              <div className="px-4 py-2 bg-[#fff8f5] border-b border-[#ffe0d5] text-xs text-[#fb6340] flex items-center gap-1.5">
                <i className="fas fa-info-circle" />You are in control — AI is paused for this customer until you release.
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? <div className="text-center py-8 text-[#8898aa] text-xs">No messages yet</div>
                : messages.map(m => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${m.role === "user" ? "bg-[#f6f9fc]" : m.role === "manager" ? "bg-[#fff0eb]" : "bg-[#eef0fd]"}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <i className={`${roleIcon(m.role)} text-xs`} style={{ color: roleColor(m.role) }} />
                        <span className="text-xs font-semibold" style={{ color: roleColor(m.role) }}>{roleLabel(m.role)}</span>
                        <span className="text-xs text-[#adb5bd]">{new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                      <p className="text-sm text-[#32325d] leading-relaxed whitespace-pre-wrap">{m.content}</p>
                    </div>
                  </div>
                ))}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-3 border-t border-[#e9ecef] bg-[#f8f9fe]">
              {!takenOver && <p className="text-xs text-[#adb5bd] text-center mb-2">Take over the conversation to send messages directly</p>}
              <div className="flex gap-2">
                <textarea value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} disabled={!takenOver} rows={2} placeholder={takenOver ? "Type a message… (Enter to send)" : "Take over first to reply"} className="flex-1 border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] resize-none disabled:bg-[#f0f0f0] disabled:text-[#adb5bd]" />
                <button onClick={send} disabled={!takenOver || !reply.trim() || sending} className="w-10 flex items-center justify-center bg-[#1171ef] text-white rounded-lg hover:bg-[#4a5fc8] disabled:opacity-40 transition-colors shrink-0">
                  {sending ? <i className="fas fa-circle-notch fa-spin text-sm" /> : <i className="fas fa-paper-plane text-sm" />}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-8"><i className="fas fa-circle-notch fa-spin text-xl text-[#1171ef]" /></div>;
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function AiBot() {
  const { data: devices } = useListDevices();
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("settings");

  useEffect(() => {
    if (devices?.length && !selectedDeviceId) setSelectedDeviceId(devices[0].id);
  }, [devices]);

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "settings", label: "Settings", icon: "fas fa-sliders-h" },
    { id: "training", label: "Training", icon: "fas fa-graduation-cap" },
    { id: "conversations", label: "Live Conversations", icon: "fas fa-comments" },
  ];

  return (
    <div>
      <PageHeader title="AI Bot" breadcrumbs={["Chatbot"]} />
      <div className="px-6 pb-6 space-y-5">

        {/* Hero */}
        <div className="bg-gradient-to-r from-[#1171ef] to-[#1171ef] rounded-xl p-5 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0"><i className="fas fa-robot text-2xl" /></div>
            <div>
              <h3 className="font-bold text-lg">WhatsApp AI Customer Service Bot</h3>
              <p className="text-white/80 text-sm mt-1 max-w-2xl">
                Start and stop the bot instantly with the power button. Train it with Q&A examples, link your website as a knowledge source, and take over any conversation live.
              </p>
            </div>
          </div>
        </div>

        {!devices?.length ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-12 text-center">
            <i className="fas fa-mobile-alt text-4xl text-[#cad1d7] mb-3 block" />
            <p className="text-[#8898aa]">No devices found. Connect a WhatsApp device first.</p>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-5">
            <div className="col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] overflow-hidden">
                <div className="px-4 py-3 border-b border-[#e9ecef] bg-[#f8f9fe]">
                  <p className="text-xs font-semibold text-[#525f7f] uppercase tracking-wider">Devices</p>
                </div>
                <div className="p-2">
                  {devices.map(d => (
                    <button key={d.id} onClick={() => setSelectedDeviceId(d.id)} className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 transition-colors ${selectedDeviceId === d.id ? "bg-[#eef0fd] text-[#1171ef]" : "text-[#525f7f] hover:bg-[#f6f9fc]"}`}>
                      <div className={`w-2 h-2 rounded-full shrink-0 ${d.status === "connected" ? "bg-[#2dce89]" : "bg-[#cad1d7]"}`} />
                      <span className="text-sm font-medium truncate">{d.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-span-3 space-y-4">
              {selectedDeviceId ? (
                <>
                  <div className="flex gap-1 bg-white rounded-xl shadow-sm border border-[#e9ecef] p-1">
                    {tabs.map(tab => (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? "bg-[#1171ef] text-white shadow-sm" : "text-[#8898aa] hover:text-[#525f7f] hover:bg-[#f8f9fe]"}`}>
                        <i className={tab.icon} />{tab.label}
                      </button>
                    ))}
                  </div>
                  {activeTab === "settings" && <SettingsPanel deviceId={selectedDeviceId} />}
                  {activeTab === "training" && <TrainingPanel deviceId={selectedDeviceId} />}
                  {activeTab === "conversations" && <ConversationsPanel deviceId={selectedDeviceId} />}
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
